import { IEncodedPersistedAction } from '@engoo/realtime-protocol/persisted';
import RealtimeError from 'app/services/rtc/RealtimeError';
import { ActionStreamEvents, IActionStream, IMetadata } from 'app/services/rtc/sync/ActionStream';
import { AbstractDocumentManager, RETRY_INTERVAL_MS } from 'app/services/rtc/sync/documentmanagers/base';
import { ActionState, Events, IDocumentManager } from 'app/services/rtc/sync/documentmanagers/index';
import { IActionHandler } from 'app/services/rtc/sync/documentmanagers/lww';
import { ConditionVariable } from 'app/utils/ConditionVariable';
import { sleep } from 'app/utils/async';
import makeDebug from 'debug';
import { Map } from 'immutable';

export interface IOTActionHandler<T, ActionT> extends IActionHandler<T, ActionT> {
  transform(x: ActionT, y: ActionT): [ActionT, ActionT];

  compose(x: ActionT, y: ActionT): ActionT;
}

interface ISequencedAction<E> {
  readonly position: number;
  readonly action: E;
}

const debug = makeDebug('app:rtc:sync:documentmanager:ot');

// What follows is a nearly pure SOCT4 implementation,
// using the specified actionHandler
//
// Unfortunately hacked together at maximum speed.
// two significant exceptions:
//
//   we execute in the global order instead of transposing new ops
//     - this facilitates non-OT LWW documents
//
//   we have a global ticket number and assign just before sending
//     - this facilitates batching of actions. When our ticket number arrives
//       we can send up every buffered local operation.

export class OTDocumentManager<DocumentT, ActionT>
  extends AbstractDocumentManager<DocumentT, ActionT>
  implements IDocumentManager<DocumentT, ActionT> {
  // Permits waiting on events to be integrated.
  private readonly cvEventIntegrated = new ConditionVariable();

  private permanentErrorBackoff: boolean = false;

  private readonly cvEventAdded = new ConditionVariable();

  // Unsubmitted, ordered. Will be rebased on new upstream action.
  private localActions: ActionT[] = [];

  // On the wire to the server. Shall not be rebased.
  private inflightAction?: ISequencedAction<ActionT>;

  // From upstream, but potentially with a gap
  private bufferedOps: Map<number, IEncodedPersistedAction & ActionT>
            = Map<number, IEncodedPersistedAction & ActionT>();

  private requestedShutdown = false;

  public constructor(
    private readonly actionHandler: IOTActionHandler<DocumentT, ActionT>,
    actionStream: IActionStream<ActionT>,
    snapshot: DocumentT,
    metadata: IMetadata | undefined,
    snapshotVersion: number = -1,
  ) {
    super(actionStream, snapshot, metadata, snapshotVersion);
  }

  public start() {
    super.start();
    this.runSubmitActionThread();

    this.onAction = this.onAction.bind(this);
    this.actionStream.on(ActionStreamEvents.ACTION, this.onAction);
  }

  public shutdown(): void {
    this.actionStream.removeListener(ActionStreamEvents.ACTION, this.onAction);

    this.requestedShutdown = true;
    this.signalAll();

    super.shutdown();
  }

  public addLocalAction(x: ActionT): void {
    debug(`addLocalOp: ${JSON.stringify(x)}`);
    this.localActions.push(x);
    this.latestSnapshot = this.actionHandler.apply(this.latestSnapshot, x, ActionState.Pending);
    this.permanentErrorBackoff = false;
    this.cvEventAdded.signal();

    this.emit(Events.CHANGED, {
      state:   this.latestSnapshot,
      actions: [x],
    });

    debug(`%c  => my view of the world is now: ${JSON.stringify(this.latestSnapshot)}`,
      'font-weight: bold');
  }

  public addLocalActionWithResult(x: ActionT): Promise<ActionT & IEncodedPersistedAction> {
    throw new Error('not implemented');
  }

  // Like onAction, but triggers an update in a batch
  protected onActions(actions: Array<IEncodedPersistedAction & ActionT>): void {
    for (const action of actions) {
      this.bufferAction(action);
    }
    this.processBuffer();
  }

  protected onAction(x: IEncodedPersistedAction & ActionT): void {
    this.bufferAction(x);
    this.processBuffer();
  }

  private bufferAction(seqAction: IEncodedPersistedAction & ActionT) {
    if (typeof seqAction.position !== 'number') {
      throw new Error('incoming action must have position');
    }

    debug(`incoming action ${JSON.stringify(seqAction)}`);
    debug(`  my timestamp = ${this.snapshotVersion}`);

    if (this.snapshotVersion > seqAction.position) {
      debug(`Received outdated event, ignoring. timestamp ${seqAction.position}' +
      ' (current version = ${this.snapshotVersion})`);
    } else {
      debug(`  => buffering event ${seqAction.position}`);
      this.bufferedOps = this.bufferedOps.set(seqAction.position, seqAction);
    }
  }

  private signalAll() {
    this.cvEventAdded.signal();
    this.cvEventIntegrated.signal();
  }

  // TODO: remove the collection nature here, and go all in on `compose` as a required aspect
  // of the IOTActionHandler interface
  private processBuffer(): void {
    // position of an event corresponds to the snapshot version established by applying that event
    // document starts at {version: 0, snapshot: ''};
    //   event {position:1, op: 'hi'}
    // produces: document{version:1, snapshot: 'hi'};
    // TODO: might be simpler if events say what snapshot they apply to instead, looking at all the arithmetic

    // Maybe we can apply buffered ops?
    debug(`maybeApplyBufferedRemoteOps: buffered=${JSON.stringify(this.bufferedOps.keySeq().toArray())}`);
    debug(`  snapshot=${JSON.stringify(this.snapshot)}`);
    debug(`  snapshotVersion = ${this.snapshotVersion}`);

    const integratedActions: ActionT[] = [];

    while (this.bufferedOps.has(this.snapshotVersion + 1)) {
      const nextOp         = this.bufferedOps.get(this.snapshotVersion + 1);
      this.bufferedOps     = this.bufferedOps.delete(this.snapshotVersion + 1);
      if (!nextOp) continue; // Possibly undefined according to type

      this.snapshot        = this.actionHandler.apply(this.snapshot, nextOp, ActionState.Confirmed);
      this.snapshotVersion = nextOp.position!;

      debug(`   - applied ${JSON.stringify(nextOp)}`);
      debug(`     snapshot=${JSON.stringify(this.snapshot)}`);
      debug(`     snapshotVersion=${this.snapshotVersion}`);

      // now that we have a new base snapshot, we need to commute all pending operations
      // past the applied op

      // TODO: maybe don't commute piecewise, sequence might be better
      let serverOp: ActionT = JSON.parse(JSON.stringify(nextOp));

      debug(`  commuting all local events`);
      debug(`  for new event: ${JSON.stringify(serverOp)}`);
      debug(`  before: ${JSON.stringify(this.localActions)}`);

      for (let i = 0; i < this.localActions.length; i++) {
        // for ot-types, yay;
        // for non-ot types (either or both non-ot are equiv.); localOp = unchanged, serverOp = unchanged
        // *but* if serverOp is OT, then it should be delivered to consumers
        // if not OT, then serverOp should not. How
        const [newServerAction, newLocalAction] =
                this.actionHandler.transform(serverOp, this.localActions[i]);

        this.localActions[i] = newLocalAction;
        serverOp             = newServerAction;
      }

      // only propagate operation if it's actually OT transformed
      // ... punch through the abstraction -- :(
      // but then the transfom method becomes simpler:
      //   user intent is preserved in both actions on both sides

      // why is this here? why can't the view reconcilers check for OT?
      // or is it misleading to push up to higher level the meaning of non-OT types
      // since they can *never* be applied straight.

      // So how bad are my lies. This operation guarantees SOCT4-ordering, adds the additional ordered
      // application. Is that just opart of the interface? What if all actions were real-OT? ...
      // then this implementation is overcomplicated, but we're still not wrong

      // what about batching and commuting:
      //   combine =
      //     let (composable, rest) = breakAt (not . isOT)
      //         onFamilies f = concatMap vals . (map f) . groupBy family
      //     in (onFamilies compose) ++ rest
      // so the orchestrator needs to know which actions are OT.

      // const transformedAction = this.actionHandler.isOT(serverOp.action) ? serverOp.action : undefined;

      // so it sucks here that we can't just apply transformed op.
      // but it's also the key of this implementation. Hurts performance *minorly*?
      // oh wait no fuck it's N^2
      // but then so is this operation inherently.
      // because rebasing can only operate on a single operation (?)
      // (what about greedy combines?, breaking only on non-OT types)
      // (differing OT-families can commute/combine, but non-OT can't).

      // ooh, keep in mind it's O(N^2) for N = localActions.length;
      // ie, integrating from a blank canvas is free
      // (though we spam each intermediate state, so condensing server ops is desirable)

      integratedActions.push(serverOp);

      debug(`  after: ${JSON.stringify(this.localActions)}`);
    }

    if (integratedActions.length !== 0) {
      this.latestSnapshot = this.computeLatest();

      this.emit(Events.REMOTE_UPDATED, {
        actions: integratedActions,
        state:   this.latestSnapshot,
      });

      debug(`signalling event integration`);
      this.cvEventIntegrated.signal();
    }
  }

  private eventSubmitted(action: ActionT & IEncodedPersistedAction) {
    if (!this.inflightAction || this.inflightAction.position !== action.position) {
      throw new Error('event submitted was not the inflightAction queue' +
                      `submitted timestamp: ${action.position}; ` +
                      `localOps timestamp=${this.inflightAction && this.inflightAction.position}`);
    }

    this.inflightAction = undefined;

    this.snapshotVersion = action.position;
    this.snapshot        = this.actionHandler.apply(this.snapshot, action, ActionState.Confirmed);

    // Because application may depend on ActionState,
    // we must regenerate the latest after changing snapshot to guarantee that
    // latest = snapshot + inflight + pending
    this.latestSnapshot = this.computeLatest();

    this.emit(Events.CHANGED, {
      actions: [action],
      state:   this.latestSnapshot,
    });

    // TODO: is this necessary?
    // Following the rule:
    //   - every time snapshotVersion changes, we wake anyone waiting on its value.
    this.cvEventIntegrated.signal();
  }

  private dropLocalActions() {
    // Give up on local actions
    this.localActions = [];
    this.inflightAction = undefined;

    // Update about the change
    this.latestSnapshot = this.computeLatest();

    this.emit(Events.CHANGED, {
      actions: [],
      state:   this.latestSnapshot,
    });

    this.cvEventIntegrated.signal();
  }

  private computeLatest(): DocumentT {
    let doc = this.snapshot;

    if (this.inflightAction) {
      doc = this.actionHandler.apply(doc, this.inflightAction.action, ActionState.Pending);
    }

    doc = this.localActions.reduce(
      (d: DocumentT, action: ActionT) =>
        this.actionHandler.apply(d, action, ActionState.Pending),
      doc);

    return doc;
  }

  private async runSubmitActionThread(): Promise<void> {
    while (!this.requestedShutdown) {
      debug(`submitActionThread top`);

      if (this.localActions.length === 0 || this.permanentErrorBackoff) {
        await this.cvEventAdded.wait();
        continue;
      }

      debug(`requesting send timeslot`);
      const { position } = await this.actionStream.lock();

      // We're not holding the lock here. A lock has been granted to us until we either fulfill it,
      // or the server timeout fires on us.

      debug(`onLock(position=${position})`);
      debug(`  localOps = ${JSON.stringify(this.localActions)}`);

      while (!this.requestedShutdown && (position !== this.snapshotVersion + 1)) {
        // So we have a bit of a problem here. We own the timeslot, but we're not up to date.
        // We need to block until we have integrated the events.

        debug(`have send timestamp=${position}; current snap=${this.snapshotVersion}; waiting`);
        await this.cvEventIntegrated.wait();
        debug(`timestamp maybe changed? current snap=${this.snapshotVersion}`);
      }

      if (this.requestedShutdown) {
        // It's possible that we're able to send the event, but there's no blocking shutdown.
        // So we just drop everything.
        break;
      }

      // Now we have all of:
      //   (1) a locked position
      //   (2) all events before that position
      //   (3) some events to insert into this position

      const combinedAction = this.localActions.reduce((x, y) => this.actionHandler.compose(x, y));

      this.localActions = [];

      const seqAction     = { position, action: combinedAction };
      this.inflightAction = seqAction;

      try {
        debug(`  sending ${JSON.stringify(seqAction)}`);
        let submittedAction;
        try {
          submittedAction = await this.actionStream.insert(seqAction.action, seqAction.position);
        } catch (e) {
          // Action must not be removed from localActions until persisted or reverted.
          this.localActions.unshift(combinedAction);

          const isRealtimeError = e instanceof RealtimeError;

          if (isRealtimeError && e.shouldRetry()) {
            debug(`    action send failing! sleeping and trying again: e=${e.message}`);

            // TODO: Some kind of increasing backoff.
            await sleep(RETRY_INTERVAL_MS);
          } else if (isRealtimeError && e.shouldDropPending()) {
            debug(`    can't write action to stream. dropping pending actions code=${e.code}`);
            this.dropLocalActions();
          } else {
            this.permanentErrorBackoff = true;
            debug(`    fatal error sending message, entering permanent backoff e=${e.message}`);
          }
          continue;
        }

        this.eventSubmitted(submittedAction);
      } finally {
        this.inflightAction = undefined;
      }
    }

    debug('submitActionThread end');
  }
}
